import { useSyncExternalStore, useCallback } from 'react'

function calculateCountdownValue(
  targetTime: Date | null,
  isProcessing: boolean
): string {
  if (isProcessing) {
    return 'Processing...'
  }

  if (!targetTime) {
    return '--:--:--'
  }

  const now = new Date()
  const diff = targetTime.getTime() - now.getTime()

  if (diff <= 0) {
    return 'Starting soon...'
  }

  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

/**
 * Hook that provides a countdown timer to a target date.
 * Uses useSyncExternalStore to subscribe to a 1-second timer.
 *
 * @param targetTime - The target Date to count down to (null if not available)
 * @param isProcessing - If true, shows "Processing..." instead of countdown
 * @returns Formatted countdown string (HH:MM:SS, "Processing...", or "--:--:--")
 */
export function useCountdown(
  targetTime: Date | null,
  isProcessing: boolean = false
): string {
  // Subscribe to an interval timer as an external store
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      // Don't set up interval if processing or no target
      if (isProcessing || !targetTime) {
        return () => {}
      }

      const interval = setInterval(onStoreChange, 1000)
      return () => clearInterval(interval)
    },
    [isProcessing, targetTime]
  )

  // getSnapshot returns the current countdown value
  // useSyncExternalStore handles memoization internally
  const getSnapshot = useCallback(
    () => calculateCountdownValue(targetTime, isProcessing),
    [targetTime, isProcessing]
  )

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
